/**
 * MetaConversionMappingPanel — per-ad-account mapping of Meta conversion events
 * to viral.partners KPI buckets (lead, call, sale, trial, subscription).
 *
 * Why this exists: dashboard heuristics (`server/services/meta-graph-service.ts`)
 * try keyword/standard-event matching to count leads, but client custom pixel
 * events often don't match (e.g. `offsite_conversion.fb_pixel_custom.zapis_konsultacja`).
 * This panel surfaces the conversion-mapping UI from `analytics-settings-modal`
 * directly in the integrations hub so users can:
 *  1. See what action_types Meta actually returned for their account (verify)
 *  2. Pick the right one for each KPI bucket
 *  3. Save it into `analytics_settings.account_configs[adAccountId]`
 *
 * The mapping is per ad account because different clients (different ad accounts)
 * use different conversion event naming conventions.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Search,
  CheckCircle2,
  AlertCircle,
  Save,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface MetaAdAccount {
  id: string;
  name: string;
}

interface AccountConfig {
  conversionLead?: string | null;
  conversionCall?: string | null;
  conversionSale?: string | null;
  conversionTrial?: string | null;
  conversionSubscription?: string | null;
}

interface AnalyticsSettings {
  selectedAdAccountIds: string[];
  accountConfigs: Record<string, AccountConfig>;
}

interface VerifyResponse {
  ok: boolean;
  found: boolean;
  count: number;
  allConversions: { type: string; count: number }[];
}

const CONVERSION_FIELDS: { key: keyof AccountConfig; label: string; hint: string }[] = [
  {
    key: "conversionLead",
    label: "Lead",
    hint: "Zdarzenie liczone jako lead (np. zapis, formularz, opt-in)",
  },
  {
    key: "conversionCall",
    label: "Rozmowa",
    hint: "Zdarzenie liczone jako booked call / schedule",
  },
  {
    key: "conversionSale",
    label: "Sprzedaż",
    hint: "Zdarzenie liczone jako zakup (jeśli różne od Purchase)",
  },
  { key: "conversionTrial", label: "Trial (SaaS)", hint: "Zdarzenie start free-trial" },
  {
    key: "conversionSubscription",
    label: "Subskrypcja (SaaS)",
    hint: "Zdarzenie aktywacji subskrypcji",
  },
];

interface Props {
  forUserId?: string | null;
}

export default function MetaConversionMappingPanel({ forUserId }: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, AccountConfig>>({});
  const [verifying, setVerifying] = useState<string | null>(null); // "accId:field"
  const [verifyResults, setVerifyResults] = useState<Record<string, VerifyResponse | null>>({});

  // GET /api/analytics-settings uses `forClientUserId`; the agency ad-accounts
  // endpoint uses `forUserId`. Honor both query-string conventions.
  const settingsParam = forUserId ? `?forClientUserId=${forUserId}` : "";
  const adAccountsParam = forUserId ? `?forUserId=${forUserId}` : "";

  // Pull current analytics_settings (account_configs + selected ad accounts)
  const { data: settings } = useQuery<AnalyticsSettings>({
    queryKey: ["analytics-settings", forUserId],
    queryFn: () => apiRequest("GET", `/api/analytics-settings${settingsParam}`),
    staleTime: 30_000,
  });

  // Pull Meta ad accounts the user has access to (for naming/display)
  const { data: adAccountsData } = useQuery<{ adAccounts: MetaAdAccount[] }>({
    queryKey: ["hub-meta-ad-accounts", forUserId],
    queryFn: () => apiRequest("GET", `/api/agency/ad-accounts${adAccountsParam}`),
    staleTime: 120_000,
    retry: false,
  });

  useEffect(() => {
    if (settings?.accountConfigs) setDrafts(settings.accountConfigs);
  }, [settings?.accountConfigs]);

  const adAccounts = adAccountsData?.adAccounts ?? [];
  const selectedAccountIds = settings?.selectedAdAccountIds ?? [];

  // Show only ad accounts that this user actually has selected for analytics
  const visibleAccounts = adAccounts.filter((a) => selectedAccountIds.includes(a.id));
  // Fallback: if there are no matched accounts but there ARE selected ones, build
  // placeholder rows from selected IDs (the names won't load if /agency/ad-accounts
  // returned empty for some reason, but the form is still usable).
  const placeholderAccounts: MetaAdAccount[] = selectedAccountIds
    .filter((id) => !adAccounts.some((a) => a.id === id))
    .map((id) => ({ id, name: id }));
  const allAccounts = [...visibleAccounts, ...placeholderAccounts];

  const verify = async (accId: string, field: keyof AccountConfig) => {
    const conversionType = drafts[accId]?.[field];
    if (!conversionType) {
      toast({
        title: "Brak nazwy konwersji",
        description:
          "Najpierw wpisz nazwę event-u (np. zapis_konsultacja) lub kliknij verify pustego pola, żeby zobaczyć dostępne event-y w Meta.",
        variant: "destructive",
      });
      return;
    }
    runVerify(accId, field, conversionType);
  };

  const peek = async (accId: string) => {
    // "Peek" mode: send any sentinel value just to fetch the available list.
    runVerify(accId, "conversionLead", "__peek__");
  };

  const runVerify = async (accId: string, field: keyof AccountConfig, conversionType: string) => {
    const key = `${accId}:${field}`;
    setVerifying(key);
    setVerifyResults((prev) => ({ ...prev, [key]: null }));
    try {
      const result = (await apiRequest("POST", "/api/analytics-settings/conversion/verify", {
        adAccountId: accId,
        conversionType,
        forClientUserId: forUserId,
      })) as VerifyResponse;
      setVerifyResults((prev) => ({ ...prev, [key]: result }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd weryfikacji";
      toast({ title: "Weryfikacja nie powiodła się", description: msg, variant: "destructive" });
    } finally {
      setVerifying(null);
    }
  };

  const pickFromList = (accId: string, field: keyof AccountConfig, type: string) => {
    setDrafts((prev) => ({
      ...prev,
      [accId]: { ...(prev[accId] || {}), [field]: type },
    }));
    // Clear the verify result for this field after picking — user picked, no need to keep the list visible
    const key = `${accId}:${field}`;
    setVerifyResults((prev) => ({ ...prev, [key]: null }));
  };

  const setField = (accId: string, field: keyof AccountConfig, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [accId]: { ...(prev[accId] || {}), [field]: value || null },
    }));
  };

  // Save current drafts via dedicated PATCH (server merges; doesn't wipe other settings).
  const saveMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PATCH", "/api/analytics-settings/account-configs", {
        forClientUserId: forUserId ?? undefined,
        accountConfigs: drafts,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analytics-settings", forUserId] });
      toast({
        title: "Zapisano",
        description:
          "Mapowanie konwersji zaktualizowane. Dashboard użyje tych ustawień przy następnym odświeżeniu.",
      });
    },
    onError: (err) => {
      toast({
        title: "Błąd zapisu",
        description: err instanceof Error ? err.message : "Nie udało się zapisać mapowania",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="mt-4 border border-white/[0.1] rounded-lg p-4 bg-white/[0.02]">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <div>
          <h4 className="text-sm font-semibold text-white">Mapowanie konwersji Meta</h4>
          <p className="text-[11px] text-white/50 mt-0.5">
            Jeśli Twoja kampania używa custom konwersji (np.{" "}
            <span className="font-mono">zapis_konsultacja</span>), podpinasz ją tutaj do KPI w
            dashboardzie.
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-white/60" />
        ) : (
          <ChevronDown className="w-4 h-4 text-white/60" />
        )}
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {allAccounts.length === 0 && (
            <div className="flex items-start gap-2 text-xs text-white/60">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-400" />
              <p>Najpierw wybierz konto reklamowe w ustawieniach analityki (⚙️ na dashboardzie).</p>
            </div>
          )}

          {allAccounts.map((acc) => {
            const cfg = drafts[acc.id] || {};
            const peekKey = `${acc.id}:conversionLead`;
            const peekResult = verifyResults[peekKey];
            return (
              <div key={acc.id} className="border border-white/[0.08] rounded-md p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-white">{acc.name}</p>
                    <p className="font-mono text-[10px] text-white/40">{acc.id}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => peek(acc.id)}
                    disabled={verifying === peekKey}
                    className="border-white/[0.15] text-white/70 hover:text-white hover:bg-white/[0.08] text-xs h-7"
                    title="Pobierz wszystkie typy konwersji jakie Meta zwróciła dla tego konta w ostatnich 30 dniach"
                  >
                    {verifying === peekKey ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <Search className="w-3 h-3 mr-1" />
                    )}
                    Pokaż dostępne konwersje
                  </Button>
                </div>

                {/* Peek result — show list of available conversion types */}
                {peekResult && (
                  <div className="bg-black/30 rounded p-2 max-h-48 overflow-auto">
                    {peekResult.allConversions.length === 0 ? (
                      <p className="text-[11px] text-white/50">
                        Brak konwersji w ostatnich 30 dniach.
                      </p>
                    ) : (
                      <>
                        <p className="text-[10px] text-white/40 mb-1.5">
                          Kliknij aby podpiąć pod KPI:
                        </p>
                        <div className="space-y-1">
                          {peekResult.allConversions.map((c) => (
                            <div
                              key={c.type}
                              className="flex items-center justify-between gap-2 text-[11px]"
                            >
                              <span className="font-mono text-white/70 truncate">{c.type}</span>
                              <div className="flex items-center gap-1 shrink-0">
                                <span className="text-white/40 tabular-nums">{c.count}</span>
                                {CONVERSION_FIELDS.map((f) => (
                                  <button
                                    key={f.key}
                                    onClick={() => pickFromList(acc.id, f.key, c.type)}
                                    className="px-1.5 py-0.5 rounded bg-white/[0.05] hover:bg-emerald-600/30 text-white/60 hover:text-emerald-300 text-[10px] transition-colors"
                                    title={`Ustaw jako ${f.label}`}
                                  >
                                    → {f.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Per-KPI inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {CONVERSION_FIELDS.map((f) => {
                    const key = `${acc.id}:${f.key}`;
                    const result = verifyResults[key];
                    return (
                      <div key={f.key} className="space-y-1">
                        <Label className="text-[11px] text-white/70 flex items-center gap-1.5">
                          {f.label}
                          {result?.found && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            value={cfg[f.key] || ""}
                            onChange={(e) => setField(acc.id, f.key, e.target.value)}
                            placeholder={f.hint}
                            className="bg-white/[0.05] border-white/[0.1] text-white text-xs h-8 font-mono"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => verify(acc.id, f.key)}
                            disabled={!cfg[f.key] || verifying === key}
                            className="border-white/[0.15] h-8 px-2 shrink-0"
                            title="Sprawdź czy ta konwersja jest w Meta i ile zwraca"
                          >
                            {verifying === key ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Search className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                        {result && result.found && (
                          <p className="text-[10px] text-emerald-400">
                            Znaleziono w Meta — {result.count} zdarzeń (30d)
                          </p>
                        )}
                        {result && !result.found && (
                          <p className="text-[10px] text-red-400">
                            Nie znaleziono w Meta. Kliknij "Pokaż dostępne konwersje" wyżej.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {allAccounts.length > 0 && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                )}
                Zapisz mapowanie
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
